import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

const CTX = createContext(null);

// Currency metadata (symbols + names for common ones). Others fall back to ISO code.
const META = {
  NGN: { symbol: "₦", name: "Naira" }, USD: { symbol: "$", name: "US Dollar" },
  EUR: { symbol: "€", name: "Euro" }, GBP: { symbol: "£", name: "British Pound" },
  GHS: { symbol: "GH₵", name: "Ghana Cedi" }, ZAR: { symbol: "R", name: "Rand" },
  KES: { symbol: "KSh", name: "Kenyan Shilling" }, EGP: { symbol: "E£", name: "Egyptian Pound" },
  MAD: { symbol: "MAD", name: "Moroccan Dirham" }, XOF: { symbol: "CFA", name: "West African CFA" },
  XAF: { symbol: "FCFA", name: "Central African CFA" }, CAD: { symbol: "C$", name: "Canadian Dollar" },
  AUD: { symbol: "A$", name: "Australian Dollar" }, CNY: { symbol: "¥", name: "Yuan" },
  JPY: { symbol: "¥", name: "Yen" }, INR: { symbol: "₹", name: "Rupee" },
  AED: { symbol: "AED", name: "UAE Dirham" }, SAR: { symbol: "SAR", name: "Saudi Riyal" },
  BRL: { symbol: "R$", name: "Brazilian Real" }, MXN: { symbol: "MX$", name: "Mexican Peso" },
  CHF: { symbol: "CHF", name: "Swiss Franc" }, SEK: { symbol: "kr", name: "Swedish Krona" },
  NOK: { symbol: "kr", name: "Norwegian Krone" }, DKK: { symbol: "kr", name: "Danish Krone" },
  RUB: { symbol: "₽", name: "Ruble" }, TRY: { symbol: "₺", name: "Lira" },
  PKR: { symbol: "₨", name: "Pakistani Rupee" }, IDR: { symbol: "Rp", name: "Rupiah" },
  PHP: { symbol: "₱", name: "Peso" }, KRW: { symbol: "₩", name: "Won" },
  SGD: { symbol: "S$", name: "Singapore Dollar" }, HKD: { symbol: "HK$", name: "HK Dollar" },
  NZD: { symbol: "NZ$", name: "NZ Dollar" }, THB: { symbol: "฿", name: "Baht" },
};

const POPULAR = ["NGN", "USD", "EUR", "GBP", "GHS", "ZAR", "KES", "CAD", "AUD", "CNY", "INR", "JPY", "AED"];

export function CurrencyProvider({ children }) {
  const [rates, setRates] = useState(null);
  const [code, setCode] = useState(() => (localStorage.getItem("jays_currency") || "NGN").toUpperCase());

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => setRates({ base: "NGN", rates: { ngn: 1 } }));
  }, []);

  const changeCurrency = (c) => {
    const up = c.toUpperCase();
    setCode(up);
    localStorage.setItem("jays_currency", up);
  };

  const convert = (nairaAmount) => {
    if (!rates?.rates) return { amount: nairaAmount, code: "NGN", symbol: "₦" };
    const rate = rates.rates[code.toLowerCase()] ?? 1;
    const converted = nairaAmount * rate;
    return { amount: converted, code, symbol: META[code]?.symbol || code };
  };

  const format = (nairaAmount, opts = {}) => {
    if (nairaAmount === null || nairaAmount === undefined) return "";
    const { amount, code: c, symbol } = convert(nairaAmount);
    // For very small numbers show more decimals; otherwise 0-2 decimals
    const digits = amount < 1 ? 4 : amount < 100 ? 2 : 0;
    try {
      const nf = new Intl.NumberFormat(undefined, { style: "currency", currency: c, minimumFractionDigits: 0, maximumFractionDigits: digits });
      return nf.format(amount);
    } catch {
      return `${symbol}${amount.toLocaleString(undefined, { maximumFractionDigits: digits })}`;
    }
  };

  const list = useMemo(() => {
    if (!rates?.rates) return POPULAR.map((c) => ({ code: c, name: META[c]?.name || c, popular: true }));
    const all = Object.keys(rates.rates).map((k) => ({ code: k.toUpperCase(), name: META[k.toUpperCase()]?.name || k.toUpperCase(), popular: POPULAR.includes(k.toUpperCase()) }));
    return all.sort((a, b) => (b.popular - a.popular) || a.code.localeCompare(b.code));
  }, [rates]);

  return (
    <CTX.Provider value={{ code, changeCurrency, format, convert, list, ready: !!rates }}>
      {children}
    </CTX.Provider>
  );
}

export const useCurrency = () => useContext(CTX);
